import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { CapacitorHttp } from '@capacitor/core';
import { OktaService } from './okta.service';
import { isAnonymousEndpoint, isProtectedEndpoint } from '../utils/auth-endpoint-policy';
import { RequestAuthDecision } from '../models/request-auth-decision.model';


@Injectable({
  providedIn: 'root'
})
export class HttpService {

  constructor(
    public alertController: AlertController,
    private oktaService: OktaService,
  ) { }

  private withTimeouts(options?: any): any {
    const requestOptions = { ...(options || {}) };
    requestOptions.connectTimeout = 25000;
    requestOptions.readTimeout = 25000;
    return requestOptions;
  }

  private async execute(method: 'GET' | 'POST' | 'DELETE', options?: any): Promise<any> {
    const requestOptions = this.withTimeouts(options);

    try {
      if (method === 'GET') {
        return await CapacitorHttp.get(requestOptions);
      }

      if (method === 'DELETE') {
        return await CapacitorHttp.delete(requestOptions);
      }

      return await CapacitorHttp.post(requestOptions);
    } catch (reason: any) {
      reason.err_msg = 'Timeout_ERROR';
      return reason;
    }
  }

  async executeAnonymous(method: 'GET' | 'POST' | 'DELETE', options?: any): Promise<any> {
    return this.execute(method, options);
  }

  async executeWithAuthPolicy(method: 'GET' | 'POST' | 'DELETE', options?: any): Promise<any> {
    const requestOptions = this.withTimeouts(options);
    const url = requestOptions.url as string;
    const normalizedMethod = method.toUpperCase() as 'GET' | 'POST' | 'DELETE';
    const headers = { ...(requestOptions.headers || {}) };
    const decision: RequestAuthDecision = {
      requestMethod: normalizedMethod,
      requestUrl: url,
      tokenAttached: false,
      preflightAction: 'none',
      retryAttempt: 0,
      finalOutcome: 'sent',
    };

    const requiresAuth = isProtectedEndpoint(url, normalizedMethod);
    const forceAnonymous = isAnonymousEndpoint(url, normalizedMethod);

    if (!requiresAuth || forceAnonymous) {
      const response = await this.execute(normalizedMethod, { ...requestOptions, headers });
      this.logDecision(decision);
      return response;
    }

    const token = await this.oktaService.getAccessTokenOrNull();
    if (!token) {
      decision.preflightAction = 'trigger-login';
      decision.finalOutcome = 'blocked-for-login';
      this.logDecision(decision);
      await this.oktaService.triggerReauthentication();
      throw {
        status: 401,
        data: { message: 'Authentication required before request.' },
      };
    }

    headers.Authorization = `Bearer ${token}`;
    decision.tokenAttached = true;

    let response = await this.execute(normalizedMethod, { ...requestOptions, headers });
    if (response?.status !== 401) {
      this.logDecision(decision);
      return response;
    }

    decision.preflightAction = 'refresh-token';
    decision.retryAttempt = 1;
    const refreshedToken = await this.oktaService.tryRefreshAccessToken();
    if (!refreshedToken) {
      decision.finalOutcome = 'failed-unauthorized';
      this.logDecision(decision);
      await this.oktaService.triggerReauthentication();
      return response;
    }

    const retryHeaders = { ...headers, Authorization: `Bearer ${refreshedToken}` };
    response = await this.execute(normalizedMethod, { ...requestOptions, headers: retryHeaders });
    decision.finalOutcome = response?.status === 401 ? 'failed-unauthorized' : 'retried';
    this.logDecision(decision);

    if (response?.status === 401) {
      await this.oktaService.triggerReauthentication();
    }

    return response;
  }

  private logDecision(decision: RequestAuthDecision): void {
    console.log('Auth request decision', {
      method: decision.requestMethod,
      url: decision.requestUrl,
      tokenAttached: decision.tokenAttached,
      preflightAction: decision.preflightAction,
      retryAttempt: decision.retryAttempt,
      finalOutcome: decision.finalOutcome,
    });
  }

  doPost(options?:any): Promise<Response> {
    return new Promise(async res => {
      const response = await this.execute('POST', options);
      res(response);
    });
  }

  doGet(options?:any): Promise<Response> {
    return new Promise(async res => {
      const response = await this.execute('GET', options);
      res(response);
    });
  }

  getDefaultHeaders(): any {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  isLoggedIn(){
    return this.oktaService.isAuth;
  }

}
